/**
 * 현장 담당자 API 클라이언트.
 * 백엔드 /v0.1/site-management/projects/:id/contacts + /contacts/:id 와 1:1 매칭.
 *
 * 운영팀 합의: 4 필드만 — 이름 / 회사 / 휴대폰 / 담당내용. user_id 연결은 미도입
 * (외부 인력 포함에 자유 텍스트가 더 유연).
 */
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';
import {
  ApiItemResponse,
  ApiListResponse,
  ApiOkResponse,
  ensureOk,
  unwrapItem,
  unwrapList,
} from './api/response';

// 6 필드 — 운영팀 합의 후 확장. responsibility 컬럼은 UI 에서 "비고" 라벨로
// 노출 (이전 "담당내용" 의미로 도입했지만 운영팀이 자유 텍스트로 더 자주 사용).
// 백엔드 컬럼명은 호환을 위해 그대로 유지.
export interface SiteContact {
  id: number;
  site_project_id: number;
  name: string;
  company?: string;
  role?: string;
  phone?: string;
  email?: string;
  responsibility?: string; // UI label: "비고"
  created_at: string;
  updated_at: string;
}

export interface CreateSiteContactRequest {
  name: string;
  company?: string;
  role?: string;
  phone?: string;
  email?: string;
  responsibility?: string;
}

export interface UpdateSiteContactRequest {
  name?: string;
  company?: string;
  role?: string;
  phone?: string;
  email?: string;
  responsibility?: string;
}

export class SiteContactService {
  static async list(projectId: number): Promise<SiteContact[]> {
    const resp = await apiClient.get<ApiListResponse<SiteContact>>(API_ENDPOINTS.SITE_MANAGEMENT.CONTACTS.LIST(projectId));
    return unwrapList(resp, '담당자 목록 조회 실패');
  }

  static async get(contactId: number): Promise<SiteContact> {
    const resp = await apiClient.get<ApiItemResponse<SiteContact>>(API_ENDPOINTS.SITE_MANAGEMENT.CONTACTS.GET(contactId));
    return unwrapItem(resp, '담당자 조회 실패');
  }

  static async create(projectId: number, data: CreateSiteContactRequest): Promise<SiteContact> {
    const resp = await apiClient.post<ApiItemResponse<SiteContact>>(API_ENDPOINTS.SITE_MANAGEMENT.CONTACTS.CREATE(projectId), data);
    return unwrapItem(resp, '담당자 등록 실패');
  }

  static async update(contactId: number, data: UpdateSiteContactRequest): Promise<SiteContact> {
    const resp = await apiClient.put<ApiItemResponse<SiteContact>>(API_ENDPOINTS.SITE_MANAGEMENT.CONTACTS.UPDATE(contactId), data);
    return unwrapItem(resp, '담당자 수정 실패');
  }

  static async delete(contactId: number): Promise<void> {
    const resp = await apiClient.delete<ApiOkResponse>(API_ENDPOINTS.SITE_MANAGEMENT.CONTACTS.DELETE(contactId));
    ensureOk(resp, '담당자 삭제 실패');
  }
}
